import {Container, Sprite} from 'pixi.js';
import {consoleLogger} from '@gongxigames/framework/core';
import {ReelSpeedConfig} from '@gongxigames/framework/slot';

export type SymbolPosition = {
    x: number;
    y: number;
};

export interface SymbolSizes {
    width: number;
    height: number;
}

export interface ReelPositionsConfig {
    x: number;
    y: number;

    symsPositions: SymbolPosition[];
}

export enum ReelBounceTime {
    normal = 300,
    turbo = 20
}

export enum FirstReelStopTime {
    normal = 1000,
    turbo = 0
}

export enum ReelsFullStopTime {
    normal = 2500
}

export enum BetweenReelsStopDelay {
    turbo = 0
}

export enum SymbolsOrdering {
    BOTTOM_TO_TOP,
    TOP_TO_BOTTOM
}

export enum ReelsOrdering {
    LEFT_TO_RIGHT,
    RIGHT_TO_LEFT
}

export class ReelConstants {
    /**
     * @deprecated
     */
    public static REELS_OFFSETS: SymbolPosition = {
        x: 0,
        y: 0
    };

    public static SYMBOL: SymbolSizes = {
        width: 0,
        height: 0
    };

    public static SYMBOL_SLOT: SymbolSizes = {
        width: 0,
        height: 0
    };

    // speed in symbols per second:
    public static REEL_SPEED = 15;
    public static REELS_ORDERING: number = ReelsOrdering.LEFT_TO_RIGHT;
    public static SYMBOLS_ORDERING: number = SymbolsOrdering.TOP_TO_BOTTOM;
    public static REEL_STOPPING_SOUND_DURATION = 150;
    public static FIRST_REEL_STOP_TIME: number = FirstReelStopTime.normal;
    public static REEL_BOUNCE_TIME: number = ReelBounceTime.normal;
    public static REEL_BOUNCE_EFFECT: any;

    public static readonly FIRST_VISIBLE_ROW: number = 2;
    public static FIRST_VISIBLE_ROW_ON_STOP = 1;
    public static ROW_AMOUNT = 0;
    public static REELS_AMOUNT = 0;

    public static REELS: ReelPositionsConfig[] = [];

    public static readonly QUICKSTOP_TIMER: number = 0;
    public static Y_ADDITIONAL_SYMBOLS_OFFSET = -2;
    public static readonly REEL_SPEED_MULTIPLIER: number = 8;
    public static readonly TURBO_REEL_SPEED_MULTIPLIER: number = this.REEL_SPEED_MULTIPLIER * 2;

    public static readonly REEL_SPEED_FACTOR: number = this.REEL_SPEED / this.REEL_SPEED_MULTIPLIER;

    // returns reel speed in pixels per millisecond
    // CLFR-1751 class methods are not iterable, but class props as functions are iterable
    public static getReelSpeed = (speed?: number): number => {
        if (speed !== undefined) {
            speed *= ReelConstants.REEL_SPEED_FACTOR;
        } else {
            speed = ReelConstants.REEL_SPEED;
        }

        return speed * ReelConstants.SYMBOL.height / 1000;
    };

    // it's time from start stopping reel till finishes the stopping tween animation
    public static getReelFullStoppingTime = (speedSettings?: ReelSpeedConfig): number => {
        const {ROW_AMOUNT, Y_ADDITIONAL_SYMBOLS_OFFSET, SYMBOL_SLOT, REEL_BOUNCE_TIME} = ReelConstants;

        return (ROW_AMOUNT + Math.abs(Y_ADDITIONAL_SYMBOLS_OFFSET)) * SYMBOL_SLOT.height / ReelConstants.getReelSpeed(speedSettings?.speed) +
            REEL_BOUNCE_TIME;
    };

    // the time interval through which we begin to stop the next reel
    public static getBetweenReelsStoppingTime = (isTurbo: boolean): number => {
        if (isTurbo) {
            return BetweenReelsStopDelay.turbo;
        }
        const {FIRST_REEL_STOP_TIME, REELS_AMOUNT, getReelFullStoppingTime} = ReelConstants;
        const timeWhenStartStoppingLastReel = ReelsFullStopTime.normal - getReelFullStoppingTime();
        const timeWhenStartStoppingFirstReel = Math.max(FIRST_REEL_STOP_TIME - getReelFullStoppingTime(), 0);

        return (timeWhenStartStoppingLastReel - timeWhenStartStoppingFirstReel) / (REELS_AMOUNT - 1);
    };

    public static getStopDelayFromReelSpeedSettings(speedSettings: ReelSpeedConfig): number {
        const calculateDelta = () => {
            const {REELS_AMOUNT, getReelFullStoppingTime} = ReelConstants;
            const timeWhenStartStoppingLastReel = speedSettings.fullStopTime - getReelFullStoppingTime(speedSettings);

            return Math.max((timeWhenStartStoppingLastReel - speedSettings.firstReelStopTime) / (REELS_AMOUNT - 1), 0);
        };

        return speedSettings.betweenReelsStopDelay === -1
            ? calculateDelta()
            : speedSettings.betweenReelsStopDelay;
    }

    public static initReelConstants = (reelsConfigSprite: Sprite, constants: typeof ReelConstants): void => {
        let reelIndex = 0;
        let currentReel;

        constants.REELS = [];

        do {
            currentReel = reelsConfigSprite.getChildByName<Container>('reel' + reelIndex);

            if (currentReel) {
                let currentSym;
                let symIndex = 0;

                constants.REELS.push({x: currentReel.x, y: currentReel.y, symsPositions: []});

                do {
                    currentSym = currentReel.getChildByName('sym' + symIndex);

                    if (currentSym) {
                        constants.REELS[reelIndex].symsPositions.push({
                            x: currentReel.x + currentSym.x,
                            y: currentReel.y + currentSym.y
                        });
                        symIndex++;
                    }
                } while (currentSym);

                reelIndex++;
            }
        } while (currentReel);

        constants.REELS_OFFSETS.x = constants.REELS[0].x;
        constants.REELS_OFFSETS.y = constants.REELS[0].y;

        let nextReelIndex = 1;

        // for independent reels
        while (constants.SYMBOL_SLOT.width === 0) {
            constants.SYMBOL_SLOT.width = constants.REELS[nextReelIndex].x - constants.REELS[0].x;
            nextReelIndex++;
        }

        // for independent reels
        if (constants.REELS[0].symsPositions.length === 1) {
            constants.SYMBOL_SLOT.height = constants.REELS[1].symsPositions[0].y - constants.REELS[0].symsPositions[0].y;
        } else if (constants.REELS[0].symsPositions.length > 1) {
            constants.SYMBOL_SLOT.height = constants.REELS[0].symsPositions[1].y - constants.REELS[0].symsPositions[0].y;
        } else {
            consoleLogger.warn('There\'s no symbols in reel 0');
        }

        constants.REELS_AMOUNT = constants.REELS.length;
        constants.ROW_AMOUNT = constants.REELS[0].symsPositions.length;
    };
}

export type ReelConstantsType = typeof ReelConstants;